<template>
  <emd-modal
    scrollable
    has-header
    has-footer
    :open="isOpen"
    @close="onClose"
  >
    <template v-slot:header>
      <h3 class="emd-text _color_default _type_caption">
        {{ $t('charge.detail.modal.retry.header') }}
      </h3>
    </template>
    <template v-slot:content>
      <emd-alert
        v-if="error"
        variant="danger"
        @cancel="error = null"
        :title="$t('account.settings.alert.error-title')"
      >
        <template slot="content">
          <p>{{ error }}</p>
        </template>
      </emd-alert>
      <div class="eu-mb_4">
        <EmdProgressIndicator
          :totalSteps="stepsData.length"
          :currentStep="currentStep === 'detail' ? 0 : 1"
          :stepsData="stepsData"
        />
      </div>
      <div v-if="currentStep === 'confirmation'">
        <emd-alert
          v-if="!antifraud"
          variant="warning"
          :title="$t('charge.detail.modal.retry.clauses.2.title')"
          :noClose="true"
        >
          <template slot="content">
            <p>
              {{ $t('charge.detail.modal.retry.clauses.2.paragraph') }}
            </p>
          </template>
        </emd-alert>
        <emd-alert
          v-if="!antifraud"
          variant="warning"
          :title="$t('charge.detail.modal.retry.clauses.3.title')"
          :noClose="true"
        >
          <template slot="content">
            <p>
              {{ $t('charge.detail.modal.retry.clauses.3.paragraph') }}
              <a
                target="_blank"
                class="emd-link"
                rel="noopener"
                href="https://pagarme.helpjuice.com/p1-transa%C3%A7%C3%B5es-e-estornos/transa%C3%A7%C3%A3o-%7C-como-reprocessar-com-e-sem-antifraude?from_search=88097469"
              >
                {{ $t('charge.detail.modal.retry.clauses.3.anchor') }}
              </a>
            </p>
          </template>
        </emd-alert>
      </div>
      <div v-if="currentStep === 'detail'">
        <emd-alert
          variant="warning"
          :title="$t('charge.detail.modal.retry.clauses.1.title')"
          :noClose="true"
        >
          <template slot="content">
            <p>
              {{ $t('charge.detail.modal.retry.clauses.1.paragraph') }}
            </p>
          </template>
        </emd-alert>
        <div class="eu-display_flex wd-100 eu-justify_space-between">
          <div class="eu-display_flex eu-column">
            <div class="eu-display_flex eu-column">
              <p class="emd-text _color_default _type_caption eu-ma_1">
                {{ $t('charge.detail.modal.retry.details.charge') }}
              </p>
              <p class="emd-text _color_default eu-ma_1">{{ charge.id }}</p>
            </div>
            <div class="eu-display_flex">
              <div class="eu-display_flex eu-column">
                <p class="emd-text _color_default _type_caption eu-ma_1">
                  {{ $t('charge.detail.modal.retry.details.card-number') }}
                </p>
                <p class="emd-text _color_default eu-ma_1">
                  {{ charge.last_transaction.card.first_six_digits }}•••• ••••{{
                    charge.last_transaction.card.last_four_digits
                  }}
                </p>
              </div>
            </div>
            <div class="eu-display_flex">
              <div class="eu-display_flex eu-column">
                <p class="emd-text _color_default _type_caption eu-ma_1">
                  {{ $t('charge.detail.modal.retry.details.holder') }}
                </p>
                <p class="emd-text _color_default eu-ma_1">
                  {{ charge.last_transaction.card.holder_name }}
                </p>
              </div>
            </div>
          </div>
          <div class="eu-display_flex eu-column eu-justify_end eu-mr_6">
            <div class="eu-display_flex eu-column">
              <p class="emd-text _color_default _type_caption eu-ma_1">
                {{ $t('charge.detail.modal.retry.details.card-brand') }}
              </p>
              <p class="emd-text _color_default eu-ma_1">
                {{ charge.last_transaction.card.brand }}
              </p>
            </div>
            <div class="eu-display_flex eu-column">
              <p class="emd-text _color_default _type_caption eu-ma_1">
                {{ $t('charge.detail.modal.retry.details.expiration') }}
              </p>
              <p class="emd-text _color_default eu-ma_1">
                {{
                  `${charge.last_transaction.card.exp_month}/${charge.last_transaction.card.exp_year}`
                }}
              </p>
            </div>
          </div>
          <div class="eu-display_flex eu-column">
            <div class="eu-display_flex eu-column">
              <p class="emd-text _color_default _type_caption eu-ma_1">
                E-mail
              </p>
              <p class="eu-ma_1">{{ charge.customer.email }}</p>
            </div>
            <div class="eu-display_flex eu-column">
              <p class="emd-text _color_default _type_caption eu-ma_1">
                {{ $t('charge.detail.modal.retry.details.installments') }}
              </p>
              <p class="eu-ma_1">{{ charge.last_transaction.installments }}</p>
            </div>
            <div class="eu-display_flex eu-column">
              <p class="emd-text _color_default _type_caption eu-ma_1">
                {{ $t('charge.detail.modal.retry.details.value') }}
              </p>
              <p class="eu-ma_1">{{ charge.amount }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="eu-display_flex eu-column wd-100">
        <div v-if="currentStep === 'detail'">
          <h3 class="emd-text _color_default _type_caption">
            {{ $t('charge.detail.modal.retry.title') }}
          </h3>
          <div class="eu-display_flex eu-column eu-row_md eu-mb_5 eu-mt_2">
            <div
              class="eu-display_flex-item eu-basis_6_md"
              v-if="charge.retry_with_antifraud.show"
            >
              <label class="emd-radio-button">
                <input
                  class="emd-radio-button__input"
                  type="radio"
                  :value="true"
                  v-model="antifraud"
                />
                <div class="emd-radio-button__area"></div>
                <span class="emd-radio-button__text emd-text _color_default">{{
                  $t('charge.detail.modal.retry.radio-label-1')
                }}</span>
              </label>
            </div>
            <div
              class="eu-display_flex-item eu-basis_6_md"
              v-if="charge.retry_without_antifraud.show"
            >
              <label class="emd-radio-button">
                <input
                  class="emd-radio-button__input"
                  type="radio"
                  :value="false"
                  v-model="antifraud"
                />
                <div class="emd-radio-button__area"></div>
                <span class="emd-radio-button__text emd-text _color_default">{{
                  $t('charge.detail.modal.retry.radio-label-2')
                }}</span>
              </label>
            </div>
          </div>
        </div>
        <div class="eu-display_flex eu-column wd-100">
          <div
            v-if="currentStep === 'detail'"
            class="eu-display_flex wd-100 eu-justify_end"
          >
            <emd-button
              color="primary"
              type="text"
              :disabled="isLoad"
              @click="onClose"
            >
              {{ $t('charge.detail.modal.retry.exit') }}
            </emd-button>
            <emd-button
              color="primary"
              @click="nextStep"
            >
              {{ $t('charge.detail.modal.retry.next') }}
            </emd-button>
          </div>
          <div
            v-if="currentStep === 'confirmation'"
            class="eu-display_flex eu-column wd-100 eu-justify_end"
          >
            <password-confirmation
              class="eu-mt_2 wd-100"
              label=""
              :value="password"
              @input="onChangePassword"
            />
            <div class="eu-display_flex wd-100 eu-justify_end">
              <emd-button
                color="primary"
                type="text"
                :disabled="isLoad"
                @click="onBack"
              >
                {{ $t('charge.detail.modal.retry.back') }}
              </emd-button>
              <emd-button
                color="primary"
                :disabled="isLoad || !password"
                @click="onSubmit"
              >
                <emd-loader
                  v-if="isLoad"
                  :type="''"
                ></emd-loader>
                <span v-if="!isLoad">
                  {{ $t('charge.detail.modal.retry.submit') }}
                </span>
              </emd-button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </emd-modal>
</template>
<script>
import PasswordConfirmation from '@/components/project/molecules/PasswordConfirmation.vue'
import {
  EmdModal,
  EmdButton,
  EmdLoader,
  EmdAlert,
  EmdProgressIndicator
} from 'emerald-vue/src/components'
export default {
  name: 'ModalChargeReprocess',
  props: {
    charge: {
      type: Object,
      required: true
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    error: [String, null],
    isLoad: {
      type: Boolean,
      required: true
    }
  },
  components: {
    EmdModal,
    EmdButton,
    PasswordConfirmation,
    EmdLoader,
    EmdAlert,
    EmdProgressIndicator
  },
  data () {
    return {
      password: '',
      stepsData: [
        {
          label: this.$t('charge.detail.modal.retry.step-1')
        },
        {
          label: this.$t('charge.detail.modal.retry.step-2')
        }
      ],
      currentStep: 'detail',
      antifraud: !this.charge.retry_without_antifraud.show
    }
  },
  methods: {
    nextStep () {
      this.currentStep = 'confirmation'
    },
    onSubmit () {
      this.$emit('submit', {
        password: window.btoa(this.password),
        antifraud_enabled: this.antifraud
      })
    },
    onChangePassword (password) {
      this.password = password
    },
    onBack () {
      this.currentStep = 'detail'
    },
    onClose () {
      this.currentStep = 'detail'
      this.$emit('close')
    }
  }
}
</script>
<style scoped>
.wd-100 {
  width: 100%;
}
.w-457 {
  width: 457px;
}
</style>
